import { useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { FormModal, ComboBox, Select, useToastActions } from '@airclou/ui';
import type { ComboBoxItem } from '@airclou/ui';
import { addWorkspaceUser, searchTenantUsers, type AddWorkspaceUserInput } from '@/api/workspaces';

interface AddUserToWorkspaceModalProps {
  workspaceId: string;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  existingUserIds?: string[];
}

export function AddUserToWorkspaceModal({ workspaceId, isOpen, onClose, onSuccess, existingUserIds = [] }: AddUserToWorkspaceModalProps) {
  const { t } = useTranslation('dashboard');
  const { success, error } = useToastActions();
  const [loading, setLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState<ComboBoxItem | null>(null);
  const [role, setRole] = useState<'admin' | 'member'>('member');

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setSelectedUser(null);
      setRole('member');
    }
  }, [isOpen]);

  // Create the fetch function for ComboBox
  const fetchUsers = useCallback(async (
    query: string,
    limit: number,
    _offset: number,
    exclude?: string[]
  ) => {
    try {
      // Combine exclude list with existing workspace users
      const allExcluded = [...existingUserIds, ...(exclude || [])];

      const result = await searchTenantUsers(query, limit, allExcluded);

      // Convert to ComboBoxItem format
      const items: ComboBoxItem[] = result.users.map(user => ({
        id: user.user_id,
        name: user.name,
        email: user.email,
        role: user.role,
        imageUrl: user.avatar_url,
        type: 'user' as const,
      }));

      return {
        items,
        total: result.total,
        hasMore: result.hasMore,
      };
    } catch (err) {
      console.error('Failed to fetch users:', err);
      return { items: [], total: 0, hasMore: false };
    }
  }, [existingUserIds]);

  const handleSubmit = async () => {
    if (!selectedUser) return;

    setLoading(true);

    try {
      const formData: AddWorkspaceUserInput = {
        user_id: selectedUser.id,
        role,
      };

      await addWorkspaceUser(workspaceId, formData);
      success(t('workspaceTabs.memberAddedToWorkspace'), t('common.success'));
      setSelectedUser(null);
      setRole('member');
      onSuccess();
      onClose();
    } catch (err: any) {
      error(err.message || t('workspaceTabs.failedToAddMember'), t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  const handleSelectionChange = (items: ComboBoxItem[]) => {
    setSelectedUser(items.length > 0 ? items[0] : null);
  };

  const canSubmit = selectedUser !== null;

  return (
    <FormModal
      isOpen={isOpen}
      onClose={onClose}
      title={t('workspaceTabs.addMemberToWorkspace')}
      size="md"
      submitText={loading ? t('common.adding') : t('workspaceTabs.addMember')}
      canSubmit={canSubmit && !loading}
      onSubmit={handleSubmit}
    >
      <div className="space-y-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            {t('workspaceTabs.selectUser')}
          </label>
          <ComboBox
            fetchItems={fetchUsers}
            selectedItems={selectedUser ? [selectedUser] : []}
            onSelectionChange={handleSelectionChange}
            multiSelect={false}
            placeholder={t('workspaceTabs.searchByNameOrEmail')}
            zIndex={10001}
          />
          <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
            {t('workspaceTabs.searchTeamMembersHelp')}
          </p>
        </div>

        <div>
          <label htmlFor="role" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            {t('common.role')}
          </label>
          <Select
            id="role"
            value={role}
            onChange={(value) => setRole(value as 'admin' | 'member')}
            options={[
              { value: 'member', label: t('common.member') },
              { value: 'admin', label: t('common.admin') },
            ]}
          />
          <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
            {t('workspaceTabs.adminsManage')}
          </p>
        </div>
      </div>
    </FormModal>
  );
}
